import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import Router, { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import {
  OnDemandCity,
  OnDemandCityConfig
} from 'src/constants/onDemandCoverage'
import { useFilterProvider } from 'src/context/FilterProvider'
import useBreakpoints from 'src/hooks/useBreakpoints'
import useLazyNamespace from 'src/hooks/useLazyNamespace'
import { numberWithCommas } from 'src/utils'
import { afterNextPaint } from 'src/utils/afterNextPaint'
import { buildOnDemandHref, getOnDemandCardThumb } from 'src/utils/onDemand'
import {
  onDemandCardClick,
  onDemandCardView
} from 'src/utils/pixels/spotmeEvents'

/**
 * Stable identifier for the on-demand card slot in the search-results
 * `scrollY` query param. Same key used by both `<OnDemandSearchCard>` (inline
 * grid card, sparse + populated paths) and `<OnDemandRecoveryCard>` (empty
 * state) — only one is ever rendered at a time per the suppression logic in
 * `SearchList`/`EmptyState`, so a single key is sufficient. Distinct from
 * listing `location.id` UUIDs so it can never collide.
 */
export const ON_DEMAND_SCROLL_KEY = 'on-demand-card'

interface OnDemandSearchCardProps {
  /** Canonical on-demand city slug (e.g. 'monterrey'). The CTA link points to
   * `/renta-mini-bodegas/almacenamiento-a-domicilio/{canonicalCity}`. */
  canonicalCity: OnDemandCity
  /** Resolved coverage config. Used for the human-readable display name in the
   * headline ("Monterrey", "Ciudad de México"). */
  cityConfig: OnDemandCityConfig
}

/**
 * Editorial cross-link card injected mid-grid in self-storage search results
 * when the current city resolves to a covered, partner-active on-demand market.
 *
 * Mirrors `LocationSearchCard`'s mobile/desktop split for back-navigation
 * continuity:
 *
 *   - **Mobile** (`isDownMd`): renders without a `<Link>` wrapper. The
 *     `onClick` handler `Router.replace`s the SOURCE search URL with
 *     `sliderOpen=true|false` and `scrollY=on-demand-card` BEFORE pushing the
 *     landing-page URL. When the user hits back from the landing page, the
 *     drawer reopens and the on-demand card scrolls itself into view via the
 *     useEffect below — same pattern as listing cards.
 *
 *   - **Desktop** (`isUpMd`): wraps the card in `<Link target="_blank">`. The
 *     landing page opens in a new tab and the original search-results tab
 *     keeps its state, so no `Router.replace` dance is needed.
 *
 * Card visual contract:
 *  - Hairline border, cream-300 ground (vs. white listing cards) and a solid
 *    blue "A domicilio" chip so the editorial intent reads at a glance.
 *  - Square photo crop centred on the person (`getOnDemandCardThumb`) — at 130px
 *    wide a van-only or off-centre shot reads as "a vehicle", not "we come
 *    to you".
 *  - One horizontal shape at every width (142px tall, 160px at `md:`), image
 *    left, copy + CTA right — identical to `LocationSearchCard` so the tile
 *    sits flush in the single-column results rail.
 *
 * The href carries `searchReturn=<encoded asPath>` so the on-demand landing
 * page can also render a "Volver a tus resultados" affordance — both paths
 * (mobile back + desktop new-tab) preserve search context.
 */
const OnDemandSearchCard = ({
  canonicalCity,
  cityConfig
}: OnDemandSearchCardProps) => {
  const { t } = useTranslation('page_mini_bodega')
  // `page_mini_bodega` is dropped from non-storage search pages'
  // serverSideTranslations (~37 kB). This card only renders on SelfStorage,
  // where the pillar namespace already loads it — so this lazy-load is a
  // safety net and satisfies the i18n coverage linter (which is
  // useLazyNamespace-aware). See docs/performance/large-page-data.md.
  useLazyNamespace('page_mini_bodega')
  const router = useRouter()
  const { shallowParams, isSliderOpen, isReady } = useFilterProvider()
  const { isDownMd: rawIsDownMd, isUpMd } = useBreakpoints()
  const ref = useRef<HTMLDivElement>(null)
  const [isClicked, setIsClicked] = useState(false)
  // SSR-safe breakpoint: MUI's useMediaQuery returns false on the server,
  // so on the first client render (before this effect runs) it returns the
  // real viewport value. Gate the render-time branch on `mounted` so SSR
  // and first hydrate always pick the desktop branch — React #418
  // otherwise on every mobile listing/search load.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  const isDownMd = mounted && rawIsDownMd

  // Bare path until mount: the prerendered HTML's `asPath` has no query, but
  // the first client render's does (every paid landing carries `?gclid=…`),
  // and React does not patch the mismatched href back. The click handler
  // rebuilds `searchReturn` from the live URL anyway.
  const href = buildOnDemandHref(
    canonicalCity,
    mounted ? router.asPath : router.asPath.split(/[?#]/)[0]
  )
  // Use `displayShortName` so the CDMX card reads "CDMX" in the tight card
  // header instead of "Ciudad de México". For Monterrey/Guadalajara,
  // displayShortName === displayName.
  const title = t('CROSS_LINK_ON_DEMAND.TITLE_CITY', {
    city: cityConfig.displayShortName
  })

  // Return-nav scroll restoration. When the user hits back from the landing
  // page, the search URL rehydrates with `?scrollY=on-demand-card` (set by
  // onClick before navigation). This effect catches that and centers the
  // card in the open mobile drawer. Mobile + drawer-open only — desktop
  // open-in-new-tab preserves position naturally, and a closed drawer means
  // the card isn't visible to scroll to.
  useEffect(() => {
    if (!isReady || !isDownMd) return
    if (!isSliderOpen) return
    if (!ref.current) return
    if (isClicked) return

    const isInViewWindow = ref.current.getBoundingClientRect().top < 0
    if (isInViewWindow) return

    if (shallowParams.scrollY === ON_DEMAND_SCROLL_KEY) {
      ref.current.scrollIntoView({ behavior: 'instant', block: 'center' })
    }
  }, [isClicked, isDownMd, isReady, isSliderOpen, shallowParams])

  // Discovery-funnel impression (FUNNELS.md F6). Viewport-gated via
  // IntersectionObserver — the card mounts inside the keepMounted mobile
  // drawer even while the drawer is closed, so a mount-time fire would
  // count cards nobody saw. The helper dedupes once per (surface, city)
  // per session, so filter-change re-mounts don't re-fire either.
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        onDemandCardView({ city: canonicalCity, surface: 'search_grid' })
        observer.disconnect()
      },
      { threshold: 0.5 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [canonicalCity])

  const onClick = async () => {
    if (isUpMd) return
    onDemandCardClick({ city: canonicalCity, surface: 'search_grid' })
    // Set isClicked FIRST so the scroll-restoration effect's guard suppresses
    // an outbound scrollIntoView mid-flight (same nuance as
    // `LocationSearchCard`). Return-nav still restores scroll because /search
    // remounts and `isClicked` resets to false.
    setIsClicked(true)

    // Persist drawer + scroll context into the search page's history entry
    // so `router.back()` from the landing page restores both. Awaited because
    // `Router.replace` is the only way to update Next's internal history
    // state (raw `history.replaceState` doesn't sync Next's `as`/`url` fields,
    // so back-nav rehydrates from the pre-replace URL and loses the params).
    let target = href
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href)
      const sliderDesired = isSliderOpen ? 'true' : 'false'
      let dirty = false
      if (currentUrl.searchParams.get('sliderOpen') !== sliderDesired) {
        currentUrl.searchParams.set('sliderOpen', sliderDesired)
        dirty = true
      }
      if (currentUrl.searchParams.get('scrollY') !== ON_DEMAND_SCROLL_KEY) {
        currentUrl.searchParams.set('scrollY', ON_DEMAND_SCROLL_KEY)
        dirty = true
      }
      if (dirty) {
        // Paint the tap first — the ad SDKs' `history` hooks make this
        // replace ~156 ms at 4× CPU (see src/utils/afterNextPaint.ts).
        await afterNextPaint()
        await Router.replace(
          `${currentUrl.pathname}${currentUrl.search}`,
          undefined,
          { shallow: true, scroll: false }
        )
      }
      // Rebuild the landing-page URL with the JUST-UPDATED source URL captured
      // in `searchReturn`. The render-time `href` was built from `router.asPath`
      // BEFORE this Router.replace ran, so its `searchReturn` still lacks
      // `sliderOpen=true` and `scrollY=on-demand-card`. If we pushed that
      // stale `href`, the breadcrumb back link on the landing page would
      // navigate back to a search URL without the drawer-open / card-scroll
      // hints, and the user would land on a closed drawer at the top of the
      // list. Recapturing here keeps the round trip continuous.
      const returnPath = `${currentUrl.pathname}${currentUrl.search}`
      target = buildOnDemandHref(canonicalCity, returnPath)
    }
    Router.push(target)
  }

  const cardContent = (
    <div
      ref={ref}
      data-testid="on-demand-search-card"
      className="group relative overflow-hidden rounded-2xl border border-ink-100/15 bg-cream-300 transition-all duration-300"
    >
      {/* Horizontal layout — single shape across mobile and desktop, matches
          LocationSearchCard. Card height steps up at `md:`; the image waits
          until `lg:` (see below) because the rail is at its narrowest right
          when `md:` first fires. */}
      <div className="flex h-[142px] md:h-[160px]">
        {/* Image steps to 200px at `lg:`, not `md:`. The rail is 45% of the
            viewport, so at 900–1023px it is only ~357–412px wide — a 200px
            image there left 122px for the copy and the CTA pill wrapped and
            got clipped by the fixed card height. Kept in lockstep with
            `LocationSearchCard` / `LogisticsSearchCard` / `SearchLoader` so
            every tile in the grid shares one image rhythm. */}
        <div className="relative w-[130px] shrink-0 overflow-hidden bg-blue-500/10 sm:w-[155px] lg:w-[200px]">
          <Image
            src={getOnDemandCardThumb(canonicalCity)}
            alt={t('CROSS_LINK_ON_DEMAND.EYEBROW')}
            fill
            sizes="(max-width: 768px) 155px, 200px"
            className="object-cover"
            loading="lazy"
          />
        </div>
        <div className="flex flex-1 flex-col justify-between border-l border-ink-100/15 p-3 md:p-4">
          <div className="min-w-0">
            {/* Solid service chip — tells the card apart from the listing
                tiles around it (which carry no chip) at a glance. */}
            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-blue-500 px-2 py-0.5 font-display text-[10px] leading-tight font-medium tracking-[0.12em] text-white-500 uppercase md:text-[11px]">
              <i
                className="si si-delivery-truck text-[11px]"
                aria-hidden="true"
              />
              {t('CROSS_LINK_ON_DEMAND.EYEBROW_SHORT')}
            </span>
            <p className="mt-1.5 line-clamp-2 font-display text-[13px] leading-[1.2] font-medium text-ink-700 md:text-base">
              {title}
            </p>
            <p className="mt-1 font-display text-[11px] leading-tight font-medium text-blue-500 italic md:text-sm">
              {t('CROSS_LINK_ON_DEMAND.PRICE_FROM_SHORT', {
                price: numberWithCommas(cityConfig.perSqmRates.twelveMonths)
              })}
            </p>
          </div>
          <span className="inline-flex w-fit items-center self-start rounded-full bg-blue-500 px-3 py-1 text-xs font-medium text-white-500 md:px-3.5 md:py-1.5 md:text-sm">
            {t('CROSS_LINK_ON_DEMAND.CTA')}
          </span>
        </div>
      </div>
    </div>
  )

  // Mobile: no Link wrapper — onClick handles the Router.replace + push so
  // the back nav restores drawer state and scroll position.
  if (isDownMd) {
    return (
      <div
        onClick={onClick}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            void onClick()
          }
        }}
        className="block h-full cursor-pointer"
      >
        {cardContent}
      </div>
    )
  }

  // Desktop: Link with new tab — the source search tab stays alive, so no
  // history dance is needed. Behavior mirrors LocationSearchCard exactly.
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="block h-full"
      onClick={() =>
        onDemandCardClick({ city: canonicalCity, surface: 'search_grid' })
      }
    >
      {cardContent}
    </Link>
  )
}

export default React.memo(OnDemandSearchCard)
